/**
 * @file TabelaGeral.js
 * @description Componente da tabela de classificação geral com expand/collapse.
 */
import { useState } from "react";
import { ChevronDown, ChevronRight, Star } from "lucide-react";
import { getEscudoUrl } from "../api/ligaApi";


const TabelaClassificacaoGeral = ({ equipes, rodadasData }) => {
  const [expandedEquipeId, setExpandedEquipeId] = useState(null);

  const handleToggleExpand = (equipeId) => {
    setExpandedEquipeId((prevId) => (prevId === equipeId ? null : equipeId));
  };

  const calcularPontuacaoTotalEquipe = (equipeId) => {
    return rodadasData
      .filter((p) => p.equipe_id && p.equipe_id.toString() === equipeId.toString())
      .reduce((acc, curr) => acc + curr.pontos, 0);
  };

  const calcularPontuacaoTotalTime = (timeId) => {
    return rodadasData
      .filter((p) => p.time_id && p.time_id.toString() === timeId.toString())
      .reduce((acc, curr) => acc + curr.pontos, 0);
  };

  const leaderboard = equipes
    .map((equipe) => ({
      ...equipe,
      pontuacaoGeral: calcularPontuacaoTotalEquipe(equipe.id),
      times: equipe.times
        .map((time) => ({
          ...time,
          pontuacaoGeral: calcularPontuacaoTotalTime(time.id),
        }))
        .sort((a, b) => b.pontuacaoGeral - a.pontuacaoGeral),
    }))
    .sort((a, b) => b.pontuacaoGeral - a.pontuacaoGeral);

  if (leaderboard.length === 0) {
    return (
      <div className="text-center py-10">
        <p className="text-gray-500 text-sm">Nenhuma equipe cadastrada.</p>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      {leaderboard.map((equipe, index) => {
        const isExpanded = expandedEquipeId === equipe.id;

        return (
          <div
            key={equipe.id}
            className="rounded-xl border overflow-hidden bg-gray-900/50 border-gray-700/50"
          >
            {/* Linha principal clicável */}
            <div
              className="flex items-center gap-2 sm:gap-3 p-3 sm:p-4 cursor-pointer hover:bg-white/5 transition-colors"
              onClick={() => handleToggleExpand(equipe.id)}
            >
              {/* Posição */}
              <div className="w-7 sm:w-8 text-center font-black text-base sm:text-lg flex-shrink-0 text-gray-500">
                {index + 1}
              </div>

              {/* Logo */}
              <img
                src={getEscudoUrl(equipe.logo_url) || "/logo-equipe-padrao.png"}
                alt=""
                className="w-9 h-9 rounded-full object-contain flex-shrink-0"
              />

              {/* Nome + Títulos */}
              <div className="flex-1 min-w-0 flex items-center gap-1.5">
                <span className="font-bold text-white text-sm sm:text-base truncate">
                  {equipe.nome}
                </span>
                {equipe.titulos > 0 && (
                  <div
                    className="relative flex items-center justify-center w-5 h-5 flex-shrink-0"
                    title={`${equipe.titulos} Títulos`}
                  >
                    <Star size={20} className="text-yellow-400 fill-yellow-400" />
                    <span className="absolute inset-0 flex items-center justify-center text-[9px] font-black text-gray-900">
                      {equipe.titulos}
                    </span>
                  </div>
                )}
              </div>

              {/* Pontuação + ícone de expand */}
              <div className="flex items-center gap-1 flex-shrink-0">
                <span className="font-black text-teal-400 text-base sm:text-lg tabular-nums">
                  {equipe.pontuacaoGeral.toFixed(2)}
                </span>
                <span className="text-gray-500 text-xs hidden sm:inline ml-0.5">pts</span>
                {isExpanded ? (
                  <ChevronDown size={15} className="text-gray-400 ml-1.5" />
                ) : (
                  <ChevronRight size={15} className="text-gray-400 ml-1.5" />
                )}
              </div>
            </div>

            {/* Detalhes expandidos */}
            {isExpanded && (
              <div className="border-t border-white/10 px-3 sm:px-4 pb-3">
                <p className="text-[11px] font-semibold text-gray-500 uppercase tracking-wider pt-3 pb-2">
                  Pontuação Acumulada por Time
                </p>
                <div className="space-y-1.5">
                  {equipe.times.map((time, tIdx) => (
                    <div
                      key={time.id}
                      className="flex items-center gap-2 bg-gray-800/60 p-2 sm:p-2.5 rounded-lg"
                    >
                      <span className="text-xs text-gray-600 w-4 text-center flex-shrink-0">
                        {tIdx + 1}
                      </span>
                      <img
                        src={getEscudoUrl(time.escudo_url)}
                        alt={`Escudo do ${time.nome_time}`}
                        className="w-6 h-6 sm:w-7 sm:h-7 rounded-full flex-shrink-0"
                      />
                      <div className="flex-1 min-w-0">
                        <p className="font-semibold text-sm text-white truncate flex items-center gap-1.5">
                          <span className="truncate">{time.nome_time}</span>
                          {time.is_captain && (
                            <span className="text-yellow-400 font-bold text-xs flex-shrink-0">
                              (C)
                            </span>
                          )}
                        </p>
                        <p className="text-xs text-gray-500 truncate">{time.nome_cartoleiro}</p>
                      </div>
                      <span className="font-bold text-teal-400 text-sm tabular-nums flex-shrink-0">
                        {time.pontuacaoGeral.toFixed(2)}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default TabelaClassificacaoGeral;
